import React, { useEffect, useState } from 'react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { toast } from "react-toastify";
import * as moment from "moment";
import Loading from "../components/Loading";

import Header from "../components/Header";
import Pagination from "../components/pagination/Pagination";

import { getUser } from "../auth/Auth";
import { downloadFile, postRequest, uploadFile } from "../routes/Routes";
import { loadFavoriteFiles } from "../common/Common";

import favoriteIcon from "../assets/images/favorite-icon.svg";
import activeFavoriteIcon from "../assets/images/active-favorite-icon.svg";
import folderIcon from "../assets/images/folder.svg";
import imageIcon from "../assets/images/image.svg";
import fileIcon from "../assets/images/file.svg";
import pdfFileIcon from "../assets/images/pdf-file.svg";
import csvfileIcon from "../assets/images/csv-file.svg";
import docfileIcon from "../assets/images/doc-file.svg";
import xlsfileIcon from "../assets/images/xls-file.svg";

import '../assets/css/ManageAdmins.css';
import '../assets/css/Dashboard.css';

function Dashboard({ selectedClient, updateCount, count }) {
    const [user, setUser] = useState(null);
    const [selectedDate, setSelectedDate] = useState({ start: null, end: null });
    const [currentPage, setCurrentPage] = useState(1);
    const [fileType, setFileType] = useState({ value: 'All Types', label: 'All Types' });
    const [searchInput, setSearchInput] = useState(null);
    const [files, setFiles] = useState([]);
    const [filteredFiles, setFilteredFiles] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [favoriteFiles, setFavoriteFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [parentFolder, setParentFolder] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [loadingPreviewFile, setLoadingPreviewFile] = useState(false);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [loadingContent, setLoadingContent] = useState(true);
    const [viewFileModal, setViewFileModal] = useState(false);
    const [sortOption, setSortOption] = useState({ value: 'Latest', label: 'Latest' });
    const [forwardFolder, setForwardFolder] = useState(null);
    const [displayStyle, setDisplayStyle] = useState("Grid");
    const uploadFileInput = React.createRef();

    useEffect(() => {
        getUser().then((data) => {
            setUser(data);
            loadFiles(selectedClient.name);
            setLoadingContent(false);
        }).catch(() => null);

        const interval = setInterval(() => {
            loadFiles(selectedClient.name);
        }, 120000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (user) {
            setForwardFolder(null);
            loadFiles(selectedClient.name);
            setLoadingContent(false);
        }
    }, [selectedClient, count]);

    useEffect(() => {
        const updateMedia = () => setIsMobile(window.innerWidth < 768);
        updateMedia();
        window.addEventListener('resize', updateMedia);
        return () => window.removeEventListener('resize', updateMedia);
    }, []);

    const loadFiles = (folderName) => {
        setLoadingFiles(true);
        let values = {
            dto: {
                name: folderName,
            },
            context: {
                action: 'List Files'
            },
            endpoint: '/api/google/folder-file-list',
            token: user.token,
        };
        postRequest(values).then(response => {
            if (response.status === 200) {
                setFiles(response.data);
                setFilteredFiles(response.data);
                setUploadedFiles([]);
                setParentFolder(folderName);
            } else {
                toast.error("An error occurred!", {
                    position: "bottom-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            }
        }).finally(() => setLoadingFiles(false));

        values = {
            email: user.email,
            context: {
                action: 'List Favorite Files'
            },
            endpoint: '/api/user-favorite-file/list',
            token: user.token
        };
        loadFavoriteFiles(values).then(data => {
            setFavoriteFiles(data);
        }).catch(() => null);
    }

    const filterFiles = (text, fileType, date) => {
        let filteredFiles;
        if (!text || text === '') {
            filteredFiles = files;
        } else {
            text = text.toLowerCase();
            filteredFiles = files.filter((file) => `${file.name}`.toLowerCase().includes(text));
        }
        if (fileType.value !== 'All Types') {
            if (fileType.value === 'Images') {
                filteredFiles = filteredFiles.filter((file) => file.mimeType.split("/")[0] === "image");
            } else if (fileType.value === 'Folders') {
                filteredFiles = filteredFiles.filter((file) => file.mimeType === "application/vnd.google-apps.folder");
            } else {
                filteredFiles = filteredFiles.filter((file) => file.mimeType.split("/")[0] !== "image" && file.mimeType !== "application/vnd.google-apps.folder");
            }
        }
        if (date.start) {
            filteredFiles = filteredFiles.filter((file) => new Date(file.modifiedTime.value) >= date.start);
        }
        if (date.end) {
            filteredFiles = filteredFiles.filter((file) => new Date(file.modifiedTime.value) <= date.end);
        }
        setFilteredFiles(filteredFiles);
    };

    const applySorting = (files, option) => {
        let sortedFiles = [...files];
        switch (option.value) {
            case 'A-Z':
                sortedFiles.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case 'Z-A':
                sortedFiles.sort((a, b) => b.name.localeCompare(a.name));
                break;
            case 'Latest':
                sortedFiles.sort((a, b) => new Date(b.modifiedTime.value) - new Date(a.modifiedTime.value));
                break;
            case 'Oldest':
                sortedFiles.sort((a, b) => new Date(a.modifiedTime.value) - new Date(b.modifiedTime.value));
                break;
            default:
                break;
        }
        return sortedFiles;
    };

    const getFileIcon = (file) => {
        if (file.mimeType === "application/vnd.google-apps.folder") {
            return folderIcon;
        }

        const ext = file.extension?.toLowerCase();

        switch (ext) {
            case "pdf":
                return pdfFileIcon;
            case "jpg":
            case "jpeg":
            case "png":
            case "gif":
            case "bmp":
            case "webp":
                return imageIcon;
            case "doc":
            case "docx":
                return docfileIcon;
            case "xls":
            case "xlsx":
                return xlsfileIcon;
            case "csv":
                return csvfileIcon;
            default:
                return fileIcon;
        }
    };

    const handleSelectCheckBox = (file) => {
        if (selectedFiles.includes(file)) {
            setSelectedFiles(values =>
                values.filter(function (selectedFile) {
                    return selectedFile !== file;
                }),
            );
        } else {
            setSelectedFiles(values => [...values, file]);
        }
    };

    const viewFile = (file) => {
        setLoadingPreviewFile(true);
        const previewableTypes = [
            'application/pdf',
            'image/jpeg',
            'image/png',
            'image/gif',
            'image/webp',
            'text/plain'
        ];

        downloadFile('/api/google/download-file/' + file.id, user.token).then(response => {
            if (response.status === 200) {
                response.data.arrayBuffer().then(buffer => {
                    const blob = new Blob([buffer], { type: file.mimeType });

                    if (previewableTypes.includes(file.mimeType)) {
                        const objectUrl = URL.createObjectURL(blob);
                        setSelectedFile({ ...file, src: objectUrl });
                        setViewFileModal(true);
                        setLoadingPreviewFile(false);
                    } else {
                        toast.info("This file can't be previewed. Downloading now...", {
                            position: "bottom-center",
                            autoClose: 3000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                        });

                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = file.name || 'download';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(url);

                        setLoadingPreviewFile(false);
                    }
                });
            } else {
                toast.error("An error occurred while fetching the file!", {
                    position: "bottom-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
                setLoadingPreviewFile(false);

            }
        }).catch(() => setLoadingPreviewFile(false));
    };

    const tableBody = (files) => {
        let body = [];
        if (files) {
            if (selectedClient.name !== parentFolder) {
                body.push(
                    <tr key="backFolder" className="file-row">
                        <td className="file-name ellipsis-text" colSpan={4} onClick={() => {
                            setForwardFolder(`${parentFolder}`);
                            loadFiles(parentFolder.substring(0, parentFolder.lastIndexOf("/")));
                        }}>
                            <img src={folderIcon} alt="Folder Icon" />
                            ...
                        </td>
                    </tr>
                );
            }
            files.forEach((file, index) => {
                if (!file.isAdminOnly) {
                    body.push(
                        <tr key={index} className="file-row">
                            <td style={{ width: "10%" }}>
                                <div className="custom-control custom-checkbox">
                                    <input type="checkbox" className="custom-control-input" id={'selectFile' + file.id}
                                        checked={selectedFiles.includes(file)}
                                        onChange={(event) => handleSelectCheckBox(file)} />
                                    <label className="custom-control-label" htmlFor={'selectFile' + file.id} />
                                </div>
                            </td>
                            <td className="file-name ellipsis-text" style={{ width: "35%" }} onClick={() => {
                                setSelectedFile(file);
                                if (file.mimeType === "application/vnd.google-apps.folder") {
                                    setForwardFolder(null);
                                    loadFiles(parentFolder + '/' + file.name);
                                } else {
                                    viewFile(file);
                                }
                            }}>
                                {file.mimeType === "application/vnd.google-apps.folder" ?
                                    <img src={folderIcon} alt="Folder Icon" />
                                    : file.mimeType.split("/")[0] === "image" ?
                                        <img src={imageIcon} alt="Image Icon" />
                                        : <img src={getFileIcon(file)} alt="File Icon" />}
                                {file.name}
                            </td>
                            <td style={{ width: "25%" }}>
                                {moment(file.modifiedTime.value).format('DD MMM YYYY, h:mm a')}
                            </td>
                            <td style={{ width: "5%" }}>
                                <button
                                    className={favoriteFiles.includes(file.id) ? "borderless-button active-favorite-button" : "borderless-button favorite-button"}
                                    type="button"
                                    onClick={() => handleFavoriteFile(file.id)}>
                                    <img src={favoriteIcon} alt="Favorite Icon" className="inactive-image" />
                                    <img src={activeFavoriteIcon} alt="Favorite Icon" className="active-image" />
                                </button>
                            </td>
                        </tr>
                    )
                }
            });
        }
        return body;
    }

    const gridBody = (files) => {
        let body = [];
        if (files) {
            if (selectedClient.name !== parentFolder) {
                body.push(
                    <div className="col client-file-container" key="backFolder">
                        <div className="d-flex">
                            <div />
                            <div>
                                <img src={folderIcon} alt="Folder Icon" />
                            </div>
                            <div className="file-name ellipsis-text" onClick={() => {
                                setForwardFolder(`${parentFolder}`);
                                loadFiles(parentFolder.substring(0, parentFolder.lastIndexOf("/")));
                            }}>...
                            </div>
                            <div />
                        </div>
                    </div>
                );
            }
            files.forEach((file, index) => {
                if (!file.isAdminOnly) {
                    body.push(
                        <div className="col client-file-container" key={index}>
                            <div className="d-flex">
                                <div>
                                    <div className="custom-control custom-checkbox">
                                        <input type="checkbox" className="custom-control-input"
                                            id={'selectClient' + file.id}
                                            checked={selectedFiles.includes(file)}
                                            onChange={(event) => handleSelectCheckBox(file)} />
                                        <label className="custom-control-label" htmlFor={'selectClient' + file.id} />
                                    </div>
                                </div>
                                <div>
                                    {file.mimeType === "application/vnd.google-apps.folder" ?
                                        <img src={folderIcon} alt="Folder Icon" />
                                        : file.mimeType.split("/")[0] === "image" ?
                                            <img src={imageIcon} alt="Image Icon" />
                                            : <img src={getFileIcon(file)} alt="File Icon" />}
                                </div>
                                <div className="file-name ellipsis-text" onClick={() => {
                                    setSelectedFile(file);
                                    if (file.mimeType === "application/vnd.google-apps.folder") {
                                        setForwardFolder(null);
                                        loadFiles(parentFolder + '/' + file.name);
                                    } else {
                                        viewFile(file);
                                    }
                                }}>
                                    {file.name}
                                    <p>{moment(file.modifiedTime.value).format('DD MMM YYYY, h:mm a')}</p>
                                </div>
                                <div>
                                    <button
                                        className={favoriteFiles.includes(file.id) ? "borderless-button active-favorite-button" : "borderless-button favorite-button"}
                                        type="button"
                                        onClick={() => handleFavoriteFile(file.id)}>
                                        <img src={favoriteIcon} alt="Favorite Icon" className="inactive-image" />
                                        <img src={activeFavoriteIcon} alt="Favorite Icon" className="active-image" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                }
            });
        }
        return body;
    }

    const handleFavoriteFile = (fileId) => {
        let tempFavoriteFiles = favoriteFiles;
        if (favoriteFiles.includes(fileId)) {
            setFavoriteFiles(values =>
                values.filter(function (favoriteFile) {
                    return favoriteFile !== fileId;
                }),
            );
            tempFavoriteFiles = tempFavoriteFiles.filter(function (favoriteFile) {
                return favoriteFile !== fileId;
            });
        } else {
            setFavoriteFiles(values => [...values, fileId]);
            tempFavoriteFiles.push(fileId);
        }
        let values = {
            email: user.email,
            fileIds: tempFavoriteFiles,
            context: {
                action: 'Update Favorite Files'
            },
            endpoint: '/api/user-favorite-file/update',
            token: user.token
        };
        postRequest(values).then(response => {
            if (response.status === 200) {
                toast.success("Successfully updated favorite files", {
                    position: "bottom-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
                updateCount();
            } else {
                toast.error("An error occurred!", {
                    position: "bottom-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            }
        });
    }

    const createFile = (event) => {
        event.preventDefault();
        let indexTotal = 0;
        for (let i = 0; i < uploadedFiles.length; i++) {
            const data = new FormData(),
                length = uploadedFiles.length;
            data.append('file', uploadedFiles[i]);
            uploadFile(data, '/api/google/create-file?parent=' + parentFolder, { token: user.token }).then(response => {
                if (response.status !== 200) {
                    toast.error("An error occurred!", {
                        position: "bottom-center",
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                }
                uploadedFiles[i].uploaded = true;
                setUploadedFiles([...uploadedFiles]);
                if (++indexTotal === length) {
                    toast.success(response.data.message, {
                        position: "bottom-center",
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                    loadFiles(parentFolder);
                }
            });
        }
    }

    const downloadFiles = () => {
        selectedFiles.forEach((file) => {
            const fileId = file?.id || file; // Handles both full object and ID
            const fileName = file?.name || "file";

            if (!fileId) return; // Prevent calls with undefined

            if (file.mimeType === "application/vnd.google-apps.folder") {
                toast.error("Cannot download folders!", { /* toast config */ });
            } else {
                downloadFile(`/api/google/download-file/${fileId}`, user.token)
                    .then(response => {
                        if (response.status === 200) {
                            response.data.arrayBuffer().then(buffer => {
                                const url = URL.createObjectURL(new Blob([buffer]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = fileName;
                                link.click();
                                URL.revokeObjectURL(url);
                            });
                        } else {
                            toast.error("An error occurred!", { /* toast config */ });
                        }
                    });
            }
        });
    };


    const uploadedFileBody = (uploadedFiles) => {
        let body = [];
        if (uploadedFiles) {
            for (let i = 0; i < uploadedFiles.length; i++) {
                body.push(
                    <div className="d-flex" key={i}>
                        <div>
                            {uploadedFiles[i].type.split('/')[0] === "image" ?
                                <img src={imageIcon} alt={"Image Icon " + i} />
                                : uploadedFiles[i].type === "application/x-zip-compressed" ?
                                    <img src={folderIcon} alt={"Folder Icon " + i} />
                                    : <img src={fileIcon} alt={"File Icon " + i} />
                            }
                        </div>
                        <div className="file-name ellipsis-text">
                            <p>{uploadedFiles[i].name}</p>
                        </div>
                        <div>
                            {uploadedFiles[i].uploaded ?
                                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" fill="#3DA80A"
                                    className="bi bi-check-circle-fill" viewBox="0 0 16 16">
                                    <path
                                        d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                                </svg>
                                : <button type="button" className="grey-button"
                                    onClick={() => setUploadedFiles([...uploadedFiles.slice(0, i), ...uploadedFiles.slice(i + 1)])}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor"
                                        className="bi bi-x-lg" viewBox="0 0 16 16">
                                        <path
                                            d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z" />
                                    </svg>
                                </button>
                            }
                        </div>
                    </div>
                )
            }
        }
        return body;
    }

    const breadcrumbBody = (parentFolder) => {
        let body = [],
            previousParent = "";
        if (parentFolder) {
            parentFolder.split("/").forEach((folder, index) => {
                previousParent += folder === selectedClient.name ? folder : ("/" + folder);
                let tempPreviousParent = previousParent;
                body.push(
                    <li className="breadcrumb-item" key={index}>
                        <a onClick={() => {
                            setForwardFolder(parentFolder);
                            setParentFolder(tempPreviousParent);
                            loadFiles(tempPreviousParent);
                        }}>{folder}</a>
                    </li>
                );
            })
        }
        return body;
    }

    if (!loadingContent && user && selectedClient) {
        const pageSize = displayStyle === "Grid" ? 18 : 10,
            firstPageIndex = (currentPage - 1) * pageSize,
            lastPageIndex = firstPageIndex + pageSize,
            currentPageFiles = filteredFiles.slice(firstPageIndex, lastPageIndex);
        return (
            <>
                <Header component="Dashboard" selectedClient={selectedClient} selectedFiles={selectedFiles}
                    basicUser={true} downloadFilesButton={downloadFiles} />
                <div className="grey-background full-height">
                    <div className="main-container">
                        <nav style={{ "--bs-breadcrumb-divider": "'>'" }} aria-label="breadcrumb">
                            <ol className="breadcrumb">
                                {breadcrumbBody(parentFolder)}
                            </ol>
                        </nav>
                        <div className="d-flex w-100 client-search-container">
                            <div className="d-flex direction-button-container">
                                <button className='black-border-button'
                                    disabled={selectedClient.name === parentFolder}
                                    onClick={() => {
                                        setForwardFolder(`${parentFolder}`);
                                        loadFiles(parentFolder.substring(0, parentFolder.lastIndexOf("/")));
                                    }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                        fill="currentColor" className="bi bi-chevron-left" viewBox="0 0 16 16">
                                        <path fillRule="evenodd"
                                            d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" />
                                    </svg>
                                </button>
                                <button className='black-border-button' disabled={!forwardFolder}
                                    onClick={() => {
                                        loadFiles(forwardFolder);
                                    }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                        fill="currentColor" className="bi bi-chevron-right" viewBox="0 0 16 16">
                                        <path fillRule="evenodd"
                                            d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
                                    </svg>
                                </button>
                            </div>
                            <div className="file-search-container">
                                <input type="label" placeholder='Search Files' className='search-input'
                                    onChange={(event) => {
                                        setSearchInput(event.target.value);
                                        filterFiles(event.target.value, fileType, selectedDate);
                                    }} />
                            </div>
                            <div className="d-flex mobile-file-search-container mb-2">
                                <div className="d-flex direction-button-container">
                                    <button className='black-border-button'
                                        disabled={selectedClient.name === parentFolder}
                                        onClick={() => {
                                            setForwardFolder(`${parentFolder}`);
                                            loadFiles(parentFolder.substring(0, parentFolder.lastIndexOf("/")));
                                        }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                            fill="currentColor" className="bi bi-chevron-left" viewBox="0 0 16 16">
                                            <path fillRule="evenodd"
                                                d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" />
                                        </svg>
                                    </button>
                                    <button className='black-border-button' disabled={!forwardFolder}
                                        onClick={() => {
                                            loadFiles(forwardFolder);
                                        }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                            fill="currentColor" className="bi bi-chevron-right" viewBox="0 0 16 16">
                                            <path fillRule="evenodd"
                                                d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z" />
                                        </svg>
                                    </button>
                                </div>
                                <div className="file-search-container">
                                    <input type="label" placeholder='Search Files' className='search-input'
                                        onChange={(event) => {
                                            setSearchInput(event.target.value);
                                            filterFiles(event.target.value, fileType, selectedDate);
                                        }} />
                                </div>
                            </div>
                            {!isMobile ? (
                                <div className="file-filter-component">
                                    <div className="d-flex client-filter-container file-filter-container">
                                        <Select
                                            className="basic-single btn w-100 select"
                                            placeholder="Latest"
                                            name="sortOption"
                                            id="sortOption"
                                            options={[
                                                { value: 'Latest', label: 'Latest' },
                                                { value: 'Oldest', label: 'Oldest' },
                                                { value: 'A-Z', label: 'A-Z' },
                                                { value: 'Z-A', label: 'Z-A' },
                                            ]}
                                            onChange={(selected) => {
                                                setSortOption(selected);
                                                const reSorted = applySorting(filteredFiles, selected);
                                                setFilteredFiles(reSorted);
                                            }}
                                        />

                                        <Select className="basic-single btn w-100 select"
                                            placeholder="All Types"
                                            name="filterType" id="filterType"
                                            options={[
                                                { value: 'All Types', label: 'All Types' },
                                                { value: 'Images', label: 'Images' },
                                                { value: 'Files', label: 'Files' },
                                                { value: 'Folders', label: 'Folders' },
                                            ]}
                                            onChange={(event) => {
                                                setFileType(values => ({
                                                    ...values,
                                                    value: event.value,
                                                    label: event.label
                                                }));
                                                filterFiles(searchInput, {
                                                    value: event.value,
                                                    label: event.label
                                                }, selectedDate);
                                            }}
                                            required />

                                        <DatePicker
                                            placeholderText="Enter date range"
                                            selectsRange={true}
                                            startDate={selectedDate.start}
                                            endDate={selectedDate.end}
                                            onChange={(update) => {
                                                setSelectedDate(values => ({ ...values, start: update[0], end: update[1] }));
                                                filterFiles(searchInput, fileType, {
                                                    start: update[0],
                                                    end: update[1]
                                                });
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="file-filter-component">
                                    <div className="client-filter-container file-filter-container">
                                        <Select
                                            className="basic-single btn w-100 select"
                                            placeholder="Latest"
                                            name="sortOption"
                                            id="sortOption"
                                            options={[
                                                { value: 'Latest', label: 'Latest' },
                                                { value: 'Oldest', label: 'Oldest' },
                                                { value: 'A-Z', label: 'A-Z' },
                                                { value: 'Z-A', label: 'Z-A' },
                                            ]}
                                            onChange={(selected) => {
                                                setSortOption(selected);
                                                const reSorted = applySorting(filteredFiles, selected);
                                                setFilteredFiles(reSorted);
                                            }} required
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    fontSize: '13px'
                                                }),
                                                option: (provided, state) => ({
                                                    ...provided,
                                                    fontSize: '13px'
                                                }),
                                                singleValue: (provided) => ({
                                                    ...provided,
                                                    fontSize: '13px'
                                                }),
                                                placeholder: (provided) => ({
                                                    ...provided,
                                                    fontSize: '13px'
                                                })
                                            }}
                                        />

                                        <Select className="basic-single btn w-100 select"
                                            placeholder="All Types"
                                            name="filterType"
                                            options={[
                                                { value: 'All Types', label: 'All Types' },
                                                { value: 'Images', label: 'Images' },
                                                { value: 'Files', label: 'Files' },
                                                { value: 'Folders', label: 'Folders' },
                                            ]}
                                            onChange={(event) => {
                                                setFileType(values => ({
                                                    ...values,
                                                    value: event.value,
                                                    label: event.label
                                                }));
                                                filterFiles(searchInput, {
                                                    value: event.value,
                                                    label: event.label
                                                }, selectedDate);
                                            }}
                                            required
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    fontSize: '13px'
                                                }),
                                                option: (provided, state) => ({
                                                    ...provided,
                                                    fontSize: '13px'
                                                }),
                                                singleValue: (provided) => ({
                                                    ...provided,
                                                    fontSize: '13px'
                                                }),
                                                placeholder: (provided) => ({
                                                    ...provided,
                                                    fontSize: '13px'
                                                })
                                            }}
                                        />
                                    </div>
                                    <DatePicker className="mt-2"
                                        placeholderText="Enter date range"
                                        selectsRange={true}
                                        startDate={selectedDate.start}
                                        endDate={selectedDate.end}
                                        onChange={(update) => {
                                            setSelectedDate(values => ({ ...values, start: update[0], end: update[1] }));
                                            filterFiles(searchInput, fileType, {
                                                start: update[0],
                                                end: update[1]
                                            });
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        {displayStyle === "Grid" ?
                            <div className="client-file-list-container">
                                <div className="row row-cols-3">
                                    {loadingFiles ? <Loading /> : gridBody(currentPageFiles)}
                                </div>
                            </div>
                            : <table className="w-100 client-table">
                                <tbody>
                                    {tableBody(currentPageFiles)}
                                </tbody>
                            </table>
                        }
                        <Pagination
                            className="pagination-bar"
                            currentPage={currentPage}
                            totalCount={filteredFiles.length}
                            pageSize={pageSize}
                            onPageChange={page => setCurrentPage(page)}
                        />
                    </div>
                    <div className="modal fade" id="newFileModal" tabIndex="-1" aria-labelledby="newFileModalLabel"
                        aria-hidden="true">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h1 className="modal-title fs-5 modelfile-title" id="newFileModalLabel">Upload New Files</h1>
                                    {/* <button type="button" className="btn-close" data-bs-dismiss="modal"
                                            aria-label="Close"/> */}
                                    <button type="button" className="close-btn" data-bs-dismiss="modal" aria-label="Close">
                                        <svg width="28" height="29" viewBox="0 0 28 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g id="vuesax/bold/close-circle">
                                                <g id="close-circle">
                                                    <path id="Vector" d="M13.9997 3.18311C7.57134 3.18311 2.33301 8.42144 2.33301 14.8498C2.33301 21.2781 7.57134 26.5164 13.9997 26.5164C20.428 26.5164 25.6663 21.2781 25.6663 14.8498C25.6663 8.42144 20.428 3.18311 13.9997 3.18311ZM17.9197 17.5331C18.258 17.8714 18.258 18.4314 17.9197 18.7698C17.7447 18.9448 17.523 19.0264 17.3013 19.0264C17.0797 19.0264 16.858 18.9448 16.683 18.7698L13.9997 16.0864L11.3163 18.7698C11.1413 18.9448 10.9197 19.0264 10.698 19.0264C10.4763 19.0264 10.2547 18.9448 10.0797 18.7698C9.74134 18.4314 9.74134 17.8714 10.0797 17.5331L12.763 14.8498L10.0797 12.1664C9.74134 11.8281 9.74134 11.2681 10.0797 10.9298C10.418 10.5914 10.978 10.5914 11.3163 10.9298L13.9997 13.6131L16.683 10.9298C17.0213 10.5914 17.5813 10.5914 17.9197 10.9298C18.258 11.2681 18.258 11.8281 17.9197 12.1664L15.2363 14.8498L17.9197 17.5331Z" fill="#B2B9C2" />
                                                </g>
                                            </g>
                                        </svg>
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <form onSubmit={createFile}>
                                        <div className="custom-file">
                                            <input type="file" className="custom-file-input"
                                                id="uploadFiles" ref={uploadFileInput} multiple
                                                onChange={(event) => setUploadedFiles(Array.from(uploadFileInput.current.files))} />
                                            <label className="custom-file-label"
                                                htmlFor="uploadFiles">
                                                <div className="image-uploader-label">
                                                    Click to upload / Drag and Drop files
                                                </div>
                                            </label>
                                        </div>
                                        <div className="uploaded-file-list-container">
                                            {uploadedFileBody(uploadedFiles)}
                                        </div>
                                        <div className="admin-form-submit-button-container modal-button-container">
                                            <button className='blue-button' type="submit">
                                                Upload
                                            </button>
                                            <button type="button" className='black-button' data-bs-dismiss="modal">
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>

                    {loadingPreviewFile && <div className="preview-loading-overlay"><Loading /></div>}

                    {viewFileModal && selectedFile && (
                        <div className="modal fade show d-block" id="viewFileModal" tabIndex="-1"
                            aria-labelledby="viewFileModalLabel"
                            role="dialog"
                            style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
                        >
                            <div className="modal-dialog modal-dialog-centered view-file-modal-dialog">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h1 className="modal-title fs-5 modelfile-title" id="viewFileModalLabel">{selectedFile.name}</h1>
                                        <button
                                            type="button"
                                            className="close-btn"
                                            onClick={() => setViewFileModal(false)}
                                            aria-label="Close"
                                        >
                                            <svg width="28" height="29" viewBox="0 0 28 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <g id="vuesax/bold/close-circle">
                                                    <g id="close-circle">
                                                        <path id="Vector" d="M13.9997 3.18311C7.57134 3.18311 2.33301 8.42144 2.33301 14.8498C2.33301 21.2781 7.57134 26.5164 13.9997 26.5164C20.428 26.5164 25.6663 21.2781 25.6663 14.8498C25.6663 8.42144 20.428 3.18311 13.9997 3.18311ZM17.9197 17.5331C18.258 17.8714 18.258 18.4314 17.9197 18.7698C17.7447 18.9448 17.523 19.0264 17.3013 19.0264C17.0797 19.0264 16.858 18.9448 16.683 18.7698L13.9997 16.0864L11.3163 18.7698C11.1413 18.9448 10.9197 19.0264 10.698 19.0264C10.4763 19.0264 10.2547 18.9448 10.0797 18.7698C9.74134 18.4314 9.74134 17.8714 10.0797 17.5331L12.763 14.8498L10.0797 12.1664C9.74134 11.8281 9.74134 11.2681 10.0797 10.9298C10.418 10.5914 10.978 10.5914 11.3163 10.9298L13.9997 13.6131L16.683 10.9298C17.0213 10.5914 17.5813 10.5914 17.9197 10.9298C18.258 11.2681 18.258 11.8281 17.9197 12.1664L15.2363 14.8498L17.9197 17.5331Z" fill="#B2B9C2" />
                                                    </g>
                                                </g>
                                            </svg>
                                        </button>
                                    </div>

                                    <div className="modal-body" style={{ padding: 0, height: '80vh' }}>
                                        <iframe
                                            src={selectedFile.src}
                                            frameBorder="0"
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                border: 'none'
                                            }}
                                            allowFullScreen
                                            title={selectedFile.name}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </>
        );
    } else (
        <Loading />
    );
}

export default Dashboard;
